import type { ComponentPropsWithRef } from 'react';

import { cn } from '../../lib/cn';

/**
 * The shimmer is the loading state, not decoration: it runs whenever a skeleton
 * is on screen, whatever the animation level. `global.css` owns the keyframe and
 * the policy — under `prefers-reduced-motion` it drops the gradient for a flat
 * `--shimmer-base` tint, so the block still reads as a loading placeholder
 * instead of as a gap.
 *
 * Colours come from the `--shimmer-base` / `--shimmer-crest` token pair, which
 * are declared per theme. The crest is always the lighter of the two in BOTH
 * themes — the previous dark pair pointed the crest at `--surface-alt`, which is
 * navy, so the dark sweep ran light → dark while the light sweep ran dark →
 * light.
 */
const SHIMMER = cn(
  // Never zero-height: a bare <Skeleton /> is still a reserved text line.
  'dhd-shimmer block h-4 w-full rounded-xs',
  '[background-image:linear-gradient(90deg,var(--shimmer-base)_25%,var(--shimmer-crest)_50%,var(--shimmer-base)_75%)]',
  '[background-size:200%_100%]',
  'animate-[dhd-shimmer_1.8s_linear_infinite]',
);

/**
 * A reserved text line, measured rather than guessed: the bar is exactly one
 * line box of `text-2xs` at the theme's body line-height. A fixed `h-3` is 12px
 * against the 16.5px line it stands in for, which is 4.5px of shift per label
 * when the real text arrives.
 */
const TEXT_LINE = 'h-[calc(var(--fs-2xs)*var(--lh-normal))]';

export type SkeletonProps = ComponentPropsWithRef<'div'>;

/**
 * A shimmering block that reserves the space the real content will take.
 * Decorative by contract — it is `aria-hidden` and never carries meaning; the
 * container that renders it declares the loading state (`aria-busy`).
 */
export function Skeleton({ className, ...rest }: SkeletonProps) {
  return <div {...rest} aria-hidden="true" className={cn(SHIMMER, className)} />;
}

/** Deterministic widths per column — a random width would re-shuffle on every
 *  render and shift the layout the skeleton exists to protect. */
const CELL_WIDTHS = ['w-[72%]', 'w-[48%]', 'w-[86%]', 'w-[58%]', 'w-[64%]', 'w-[38%]'];

export interface SkeletonMenuProps extends Omit<ComponentPropsWithRef<'div'>, 'children'> {
  /** Number of menu rows to reserve. */
  rows?: number;
}

/**
 * Menu placeholder: a leading glyph square plus two text lines of differing
 * width, each row reserving the same height a real menu row uses so the list
 * does not grow when the items arrive.
 */
export function SkeletonMenu({ rows = 5, className, ...rest }: SkeletonMenuProps) {
  return (
    <div {...rest} aria-busy="true" className={cn('flex w-full flex-col', className)}>
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className="flex h-[var(--control-h-lg)] items-center gap-3">
          <Skeleton className="size-6 shrink-0" />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Skeleton className="h-2.5 w-[60%]" />
            <Skeleton className="h-2 w-[38%]" />
          </div>
        </div>
      ))}
    </div>
  );
}

export interface SkeletonTableProps extends Omit<ComponentPropsWithRef<'div'>, 'rows' | 'columns'> {
  /** Page size of the table being replaced. */
  rows?: number;
  /** Visible column count of the table being replaced. */
  columns?: number;
  /** Render the placeholder header strip. False when the real table keeps its own
   *  `<thead>` mounted in the loading state (§11), which would otherwise double it. */
  showHeader?: boolean;
}

/**
 * Table placeholder shaped like the table it replaces — a header strip plus
 * fixed-height body rows in a real `<table>`, so the column widths and the total
 * height are the same before and after the data lands.
 */
export function SkeletonTable({ rows = 8, columns = 6, showHeader = true, className, ...rest }: SkeletonTableProps) {
  const cols = Array.from({ length: Math.max(1, columns) });

  return (
    <div
      {...rest}
      aria-busy="true"
      className={cn('w-full overflow-hidden rounded-xs border border-line-subtle', className)}
    >
      {/* Hidden from the tree: it would otherwise expose an empty table with
          column headers. The wrapper's `aria-busy` is the state signal. */}
      <table aria-hidden="true" className="w-full table-fixed border-collapse">
        <thead>
          {showHeader ? (
            <tr className="h-[var(--control-h-lg)] border-b border-line-subtle bg-surface-muted">
              {cols.map((_, col) => (
                <th key={col} scope="col" className="px-4">
                  <Skeleton className={cn('h-2.5', CELL_WIDTHS[col % CELL_WIDTHS.length])} />
                </th>
              ))}
            </tr>
          ) : null}
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, row) => (
            <tr
              key={row}
              className="h-[var(--control-h-lg)] border-b border-line-subtle last:border-b-0"
            >
              {cols.map((_, col) => (
                <td key={col} className="px-4">
                  <Skeleton className={cn('h-3', CELL_WIDTHS[col % CELL_WIDTHS.length])} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export interface SkeletonKpiCardsProps extends Omit<ComponentPropsWithRef<'div'>, 'children'> {
  /** Number of KPI cards to reserve. */
  count?: number;
}

/**
 * KPI card placeholder: the label line, the display-value line and the delta
 * line, at the real card's padding and rhythm — so a row of cards keeps the same
 * height while the metrics load.
 *
 * The three bars and the two gaps are taken from `Metric`'s own geometry, not
 * approximated: the label is one `text-2xs` line box (16.5px), the value is the
 * `text-3xl` display line (44px, `h-11`), the delta is the `text-xs` line with
 * its `min-h-4` floor (18px), and the gaps are the `mt-2` / `mt-1.5` the real
 * component uses. The old 12 / 32 / 12 bars on a flat `gap-3` reserved 122px
 * against the real card's 134px, so every KPI row grew 12px the moment it
 * loaded.
 *
 * The grid tops out at two columns deliberately: this placeholder is dropped
 * into containers as narrow as 566px, and a four-column grid there gave 130px
 * cards in which the real value and delta wrap to two and three lines. No
 * skeleton can predict that; not making cards 130px wide is the fix.
 */
export function SkeletonKpiCards({ count = 4, className, ...rest }: SkeletonKpiCardsProps) {
  return (
    <div
      {...rest}
      aria-busy="true"
      className={cn('grid w-full grid-cols-1 gap-4 sm:grid-cols-2', className)}
    >
      {Array.from({ length: count }, (_, card) => (
        <div
          key={card}
          className="flex flex-col rounded-xs border border-line-subtle bg-surface p-5"
        >
          <Skeleton className={cn(TEXT_LINE, 'w-[45%]')} />
          <Skeleton className="mt-2 h-11 w-[65%]" />
          <Skeleton className="mt-1.5 h-4.5 w-[30%]" />
        </div>
      ))}
    </div>
  );
}

export interface SkeletonFormProps extends Omit<ComponentPropsWithRef<'div'>, 'children'> {
  /** Number of labelled fields to reserve. */
  fields?: number;
}

/**
 * Form placeholder: a label line over a control-height block, repeated — the
 * labelled-field height the rest of the system uses, so the submit button does
 * not jump down when the real fields render.
 *
 * The label bar is the real label's line box (`TEXT_LINE`) and the gap is
 * `gap-1.5`, which is the gap `Field` itself uses. The old 12px bar over an 8px
 * gap reserved 60px per field against the real 62.5px, so four fields pushed the
 * submit button 10px down; the pair now measures 62.5px and the total matches
 * the real form to the pixel.
 */
export function SkeletonForm({ fields = 4, className, ...rest }: SkeletonFormProps) {
  return (
    <div {...rest} aria-busy="true" className={cn('flex w-full flex-col gap-5', className)}>
      {Array.from({ length: fields }, (_, field) => (
        <div key={field} className="flex flex-col gap-1.5">
          <Skeleton className={cn(TEXT_LINE, 'w-24')} />
          <Skeleton className="h-[var(--control-h-md)] w-full" />
        </div>
      ))}
    </div>
  );
}
